import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Clock, Calendar, Power, PowerOff, Trash2, Save, CalendarRange } from 'lucide-react';

const DAYS = [
  { value: 0, label: 'Mon' },
  { value: 1, label: 'Tue' },
  { value: 2, label: 'Wed' },
  { value: 3, label: 'Thu' },
  { value: 4, label: 'Fri' },
  { value: 5, label: 'Sat' },
  { value: 6, label: 'Sun' }
];

const ScheduleConfig = ({ targetId, targetType }) => {
  const { t } = useTranslation();
  const [schedule, setSchedule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedDays, setSelectedDays] = useState([0, 2, 4]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    loadSchedule();
  }, [targetId]);

  const loadSchedule = async () => {
    try {
      const { data } = await axios.get(
        `${API_URL}/api/schedules?target_id=${targetId}`,
        { withCredentials: true }
      );
      const existing = data.find(s => s.target_id === targetId && s.target_type === targetType);
      if (existing) {
        setSchedule(existing);
        setSelectedDays(existing.days_of_week);
        setStartDate(existing.start_date || '');
        setEndDate(existing.end_date || '');
      }
    } catch (error) {
      console.error('Error loading schedule:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleDay = (day) => {
    setSelectedDays(prev =>
      prev.includes(day)
        ? prev.filter(d => d !== day)
        : [...prev, day].sort()
    );
  };

  const handleSave = async () => {
    if (selectedDays.length === 0 || !startDate || !endDate) return;
    setSaving(true);
    try {
      if (schedule) {
        const { data } = await axios.put(
          `${API_URL}/api/schedules/${schedule.id}`,
          { days_of_week: selectedDays, active: true, start_date: startDate, end_date: endDate },
          { withCredentials: true }
        );
        setSchedule(data);
      } else {
        const { data } = await axios.post(
          `${API_URL}/api/schedules`,
          { target_id: targetId, target_type: targetType, days_of_week: selectedDays, start_date: startDate, end_date: endDate },
          { withCredentials: true }
        );
        setSchedule(data);
      }
    } catch (error) {
      console.error('Error saving schedule:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async () => {
    if (!schedule) return;
    setSaving(true);
    try {
      const { data } = await axios.put(
        `${API_URL}/api/schedules/${schedule.id}`,
        { active: !schedule.active },
        { withCredentials: true }
      );
      setSchedule(data);
    } catch (error) {
      console.error('Error toggling schedule:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!schedule) return;
    setSaving(true);
    try {
      await axios.delete(
        `${API_URL}/api/schedules/${schedule.id}`,
        { withCredentials: true }
      );
      setSchedule(null);
      setSelectedDays([0, 2, 4]);
      setStartDate('');
      setEndDate('');
    } catch (error) {
      console.error('Error deleting schedule:', error);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return null;

  const canSave = selectedDays.length > 0 && startDate && endDate && endDate >= startDate;

  return (
    <div className="bg-white border border-[#E2DDD8] mb-12" data-testid="schedule-config">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full p-6 flex items-center justify-between hover:bg-slate-50 transition-colors"
        data-testid="schedule-toggle-button"
      >
        <div className="flex items-center gap-3">
          <Clock className="w-5 h-5 text-[#1BB594]" />
          <span className="text-lg font-bold text-slate-900">{'Automatic Monitoring'}</span>
          {schedule && (
            <span className={`text-xs font-bold uppercase tracking-widest px-2 py-1 ${
              schedule.active
                ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                : 'bg-slate-50 text-slate-500 border border-slate-200'
            }`}>
              {schedule.active ? 'Active' : 'Paused'}
            </span>
          )}
        </div>
        <span className="text-sm text-slate-400">{isOpen ? '\u25B2' : '\u25BC'}</span>
      </button>

      {isOpen && (
        <div className="px-6 pb-6 border-t border-[#E2DDD8] pt-6">
          {/* Fixed time notice */}
          <div className="mb-6 p-3 bg-[#F5F3EE] border border-[#E2DDD8]">
            <p className="text-sm text-slate-700">
              <Clock className="w-4 h-4 inline mr-1 text-[#1BB594]" />
              Monitoring queue runs daily at <strong>01:00 UTC</strong>
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-6">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-3 flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                MONITORING DAYS
              </p>
              <div className="flex gap-2" data-testid="schedule-days">
                {DAYS.map(day => (
                  <button
                    key={day.value}
                    onClick={() => toggleDay(day.value)}
                    className={`w-10 h-10 text-sm font-bold border transition-colors ${
                      selectedDays.includes(day.value)
                        ? 'bg-[#1BB594] text-white border-[#1BB594]'
                        : 'bg-white text-slate-600 border-[#E2DDD8] hover:bg-slate-50'
                    }`}
                    data-testid={`schedule-day-${day.value}`}
                  >
                    {day.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-3 flex items-center gap-2">
                <CalendarRange className="w-4 h-4" />
                ACTIVE PERIOD
              </p>
              <div className="flex items-center gap-3">
                <div>
                  <label className="text-xs text-slate-500 mb-1 block">Start</label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="border border-[#E2DDD8] px-3 py-2 text-sm font-semibold text-slate-900 bg-white"
                    data-testid="schedule-start-date"
                  />
                </div>
                <span className="text-slate-400 mt-4">&mdash;</span>
                <div>
                  <label className="text-xs text-slate-500 mb-1 block">End</label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    min={startDate}
                    className="border border-[#E2DDD8] px-3 py-2 text-sm font-semibold text-slate-900 bg-white"
                    data-testid="schedule-end-date"
                  />
                </div>
              </div>
              {startDate && endDate && endDate < startDate && (
                <p className="text-xs text-red-500 mt-1">End date cannot be earlier than start date</p>
              )}
            </div>
          </div>

          <div className="flex items-center gap-3 pt-6 border-t border-[#E2DDD8]">
            <button
              onClick={handleSave}
              disabled={saving || !canSave}
              className="rounded-none font-bold tracking-widest uppercase text-xs px-6 py-3 bg-[#1BB594] hover:bg-[#159A7F] text-white transition-transform active:translate-y-1 flex items-center gap-2 disabled:opacity-50"
              data-testid="schedule-save-button"
            >
              <Save className="w-4 h-4" />
              {saving ? 'SAVING...' : 'SAVE'}
            </button>

            {schedule && (
              <>
                <button
                  onClick={handleToggleActive}
                  disabled={saving}
                  className={`rounded-none font-bold tracking-widest uppercase text-xs px-6 py-3 border transition-colors flex items-center gap-2 disabled:opacity-50 ${
                    schedule.active
                      ? 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100'
                      : 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100'
                  }`}
                  data-testid="schedule-toggle-active-button"
                >
                  {schedule.active ? <PowerOff className="w-4 h-4" /> : <Power className="w-4 h-4" />}
                  {schedule.active ? 'PAUSE' : 'ENABLE'}
                </button>

                <button
                  onClick={handleDelete}
                  disabled={saving}
                  className="rounded-none font-bold tracking-widest uppercase text-xs px-6 py-3 bg-red-50 text-red-700 border border-red-200 hover:bg-red-100 transition-colors flex items-center gap-2 disabled:opacity-50"
                  data-testid="schedule-delete-button"
                >
                  <Trash2 className="w-4 h-4" />
                  DELETE
                </button>
              </>
            )}
          </div>

          {schedule && (
            <div className="mt-4 text-xs text-slate-500">
              {schedule.active
                ? `Period: ${schedule.start_date || '?'} \u2014 ${schedule.end_date || '?'} | ${DAYS.filter(d => selectedDays.includes(d.value)).map(d => d.label).join(', ')} at 01:00 UTC`
                : 'Schedule is paused'}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ScheduleConfig;
